'use strict';

import DistanceControllerService from "./service";
import HandControllerData from "../avatar/handControllerData";
import {AvatarController} from "../avatar";
import OnBoardTutorialController from "../onBoardTutorial/controller";

class DistanceController {
    constructor() {
        this.distanceLasers = {};

        this.cameraYOffset = 0.1;

        this.selectionCircleScaleX = 0.012;
        this.selectionCircleScaleY = 0.012;
        this.selectionCircleScaleZ = 0.012;

        this.tooltipOffset = 0.5;

        this.mouseControllerObject = iq3Camera.getCamera();

        this.menu = undefined;

        this.distanceControllerService = new DistanceControllerService();
        this.handControllerData = new HandControllerData();
    }

    initMenu(parentMenu, prop, isPresenter) {
        if (!this.menu) {
            this.menu = new MenuData(DistanceFromControllerMenu.text, 4, 4);

            let controller = new MenuController(this.menu);
            controller.setMenuControlParameter(VR.scene, iq3Camera.camera, wv.selectionSphere);
            this.menu.selectionCloseMenuForSingleSelection = true;

            parseChildrenItems(controller, prop.children, menuItemMaps.distanceFromControllerMenuItemMap);
        }
        this.menu.isVisible = prop.isVisible;
        this.menu.isPresenter = prop.isPresenter;
        this.menu.isViewer = prop.isViewer;

        parentMenu.appendMenuItem(DistanceFromControllerMenu.menuItemName, prop.text || DistanceFromControllerMenu.text, this.menu);
    }

    getControllerName(controller) {
        let controllerName = null;
        if (controller === wv.controllerLeft)
            controllerName = this.handControllerData.vive1Name;
        else if (controller === wv.controllerRight)
            controllerName = this.handControllerData.vive2Name;
        else if (controller.name !== undefined && controller.name !== "")
            controllerName = controller.name;

        return controllerName;
    }

    showDistanceFromController(controller, point, tooltip, selectionCircle) {
        const distance = calculateDistance(getWorldPositionThreeJS(controller), point);
        let selectedPoint = point.clone();
        selectedPoint.y += this.tooltipOffset;
        tooltip.setPosition(selectedPoint);
        tooltip.setText(distance.toFixed(2));
        tooltip.show(true);

        selectionCircle.position.copy(point);
        this.scaleSelectionCircle(selectionCircle, distance);
        selectionCircle.visible = true;
    }

    addToDistanceLasers(controllerId, line, tooltip, selectedPoint, branchId, userId, selectionCircle) {
        if (this.distanceLasers[controllerId] === undefined) {
            this.distanceLasers[controllerId] = [];
        }

        const volume = modelManager.getVolume(branchId);

        this.distanceLasers[controllerId].push({
            line: line,
            tooltip: tooltip,
            selectedPoint: selectedPoint,
            volumeName: volume.name,
            branchId: volume.branchID,
            modelName: volume.modelName,
            modelId: volume.modelId,
            userId: userId,
            selectionCircle: selectionCircle
        });
    }

    getLaserCount(controllerId) {
        let count = 0;
        if (this.distanceLasers[controllerId] !== undefined) {
            count = this.distanceLasers[controllerId].length;
        }

        return count;
    }

    addDistancePointToMovementHandler(bodyName) {
        // this is for after moving a model
        const volume = modelManager.getVolume(bodyName);
        addHandlerForOperation(volume.modelId, [volume.modelId, bodyName], this.moveDistancePoint.bind(this),
            volume.modelId + "_distancePoint", HANDLER_TYPE_ENUM.DISTANT_POINT, HANDLER_TYPE_ENUM.MODEL_MOVE_FOR_SCENE_OBJECT);
        // this is for after moving a volume
        addHandlerForOperation(bodyName, [bodyName], this.moveDistancePoint.bind(this),
            bodyName + "_distancePoint", HANDLER_TYPE_ENUM.DISTANT_POINT, HANDLER_TYPE_ENUM.VOLUME_MOVE_FOR_SCENE_OBJECT);
    }

    createSinglePointDistance(userId, controller, selectedPoint, branchId) {
        let lineColor = getDrawColorForUser(userId);
        let firstVertex = getWorldPositionThreeJS(controller);
        if (controller === this.mouseControllerObject) {
            firstVertex.y -= this.cameraYOffset;
        }

        firstVertex = VR.scene.worldToLocal(firstVertex);
        let secondVertex = selectedPoint.clone();
        secondVertex = VR.scene.worldToLocal(secondVertex);
        let geometry = new THREE.BufferGeometry();
        geometry.setFromPoints([firstVertex, secondVertex]);
        let material = new THREE.LineBasicMaterial({color: lineColor, linewidth: 3});
        let line = new THREE.Line(geometry, material);
        line.name = controller.id + this.getLaserCount(controller.id);
        line.visible = true;

        VR.scene.add(line);

        const tooltip = new TooltipControl(VR.scene);
        let selectionCircle = new IntersectionPointer(VR.scene);
        this.showDistanceFromController(controller, selectedPoint, tooltip, selectionCircle);

        this.addToDistanceLasers(controller.id, line, tooltip, selectedPoint, branchId, userId, selectionCircle);
        this.addDistancePointToMovementHandler(branchId);
    }

    addSinglePointDistanceForController(controller) {
        return this.handleAddingNewSinglePointDistance(controller, controller.pickingData);
    }

    addSinglePointDistanceForMouse(mouseEvent) {
        return this.handleAddingNewSinglePointDistance(this.mouseControllerObject, mouseEvent.pickingData);
    }

    handleAddingNewSinglePointDistance(controller, highlightedMesh) {
        if (!highlightedMesh || !highlightedMesh.volume) {
            this.deleteLastDistancePoint(controller);
            return true;
        }

        if (wv.effectController.SinglePointDistance === true)
            this.deleteLastDistancePoint(controller);

        this.createSinglePointDistance(wv.registeredID.toString(), controller, highlightedMesh.point, highlightedMesh.volume.branchID);
        const controllerName = this.getControllerName(controller);

        if (controllerName !== null) {
            const payload = {
                id: wv.registeredID,
                controllerName: controllerName,
                point: highlightedMesh.point,
                branchId: highlightedMesh.volume.branchID,
                modelId: highlightedMesh.modelId
            };

            this.distanceControllerService.addNewDistanceFromController(payload);
        }

        return true;
    }

    addSinglePointDistanceFromServer(userId, controllerName, selectedPoint, branchId) {
        let user_mesh = AvatarController.getUserMeshObjectByName(userId);

        if (user_mesh) {
            let controller = user_mesh.getObjectByName(controllerName);

            if (controller) {
                this.createSinglePointDistance(userId, controller, selectedPoint, branchId);
            }
        }
    }

    updateLaser(controller, entry) {
        let firstVertex = getWorldPositionThreeJS(controller);
        if (controller === this.mouseControllerObject) {
            firstVertex.y -= this.cameraYOffset;
        }

        firstVertex = VR.scene.worldToLocal(firstVertex);
        entry.line.geometry.attributes.position.array[0] = firstVertex.x;
        entry.line.geometry.attributes.position.array[1] = firstVertex.y;
        entry.line.geometry.attributes.position.array[2] = firstVertex.z;
        entry.line.geometry.attributes.position.needsUpdate = true;
        entry.line.updateMatrixWorld(true);

        this.showDistanceFromController(controller, entry.selectedPoint, entry.tooltip, entry.selectionCircle);
    }

    updateDistanceLasers(controller) {
        if (this.distanceLasers[controller.id] === undefined) {
            return;
        }

        controller.updateMatrixWorld(true);
        for (let i in this.distanceLasers[controller.id]) {
            this.updateLaser(controller, this.distanceLasers[controller.id][i]);
        }
    }

    deleteDistancePoint(controller, excludeHelperObjects = false) {
        if (this.distanceLasers[controller.id] && this.distanceLasers[controller.id].length > 0) {
            const length = this.distanceLasers[controller.id].length;
            if (excludeHelperObjects && modelManager.getModelType(this.distanceLasers[controller.id][length - 1].modelId) === ModelTypeEnum.HELPER_OBJECT) {
                return;
            }

            const entry = this.distanceLasers[controller.id].pop();
            this.removeThisDistanceEntry(entry);
        }
    }

    deleteLastDistancePoint(controller) {
        if (this.distanceLasers[controller.id] && this.distanceLasers[controller.id].length > 0) {
            this.deleteDistancePoint(controller);

            let controllerName = this.getControllerName(controller);
            if (controllerName !== null) {
                this.distanceControllerService.deleteLastDistancePoint({
                    userId: wv.registeredID,
                    controllerName: controllerName
                });
            }
        }
    }

    deleteLastDistancePointFromServer(userId, controllerName) {
        const user_mesh = AvatarController.getUserMeshObjectByName(userId);

        if (user_mesh) {
            const controller = user_mesh.getObjectByName(controllerName);

            if (controller) {
                this.deleteDistancePoint(controller);
            }
        }
    }

    deleteAllDistancePointOfThisController(controller) {
        if (this.distanceLasers[controller.id]) {
            while (this.distanceLasers[controller.id].length > 0) {
                this.deleteLastDistancePoint(controller);
            }
        }
    }

    deleteAllDistancePoints() {
        if (wv.controllerLeft && wv.controllerLeft.getGamepad()) {
            this.deleteAllDistancePointOfThisController(wv.controllerLeft);
        }

        if (wv.controllerRight && wv.controllerRight.getGamepad()) {
            this.deleteAllDistancePointOfThisController(wv.controllerRight);
        }

        this.deleteAllDistancePointOfThisController(this.mouseControllerObject);
    }

    removeAllDistanceFromControllers(excludeHelperObjects = false) {
        for (let controllerId in this.distanceLasers) {
            let controller = VR.scene.getObjectById(parseInt(controllerId));
            const length = this.distanceLasers[controller.id].length;
            let processed = 0;
            while (processed < length) {
                this.deleteDistancePoint(controller, excludeHelperObjects);
                processed++;
            }
        }
    }

    removeThisDistanceEntry(entry) {
        VR.scene.remove(entry.line);
        disposeObject(entry.line);
        VR.scene.remove(entry.selectionCircle);
        disposeObject(entry.selectionCircle);
        VR.scene.remove(entry.tooltip.getTooltipObject());
        removeThisTooltip(entry.tooltip);
        disposeObject(entry.tooltip.getTooltipObject());
    }

    removeDistanceFromControllerOfThisModel(modelId) {
        for (let controllerId in this.distanceLasers) {
            for (let i = this.distanceLasers[controllerId].length - 1; i >= 0; --i) {
                if (this.distanceLasers[controllerId][i].modelId === modelId) {
                    this.removeThisDistanceEntry(this.distanceLasers[controllerId][i]);
                    this.distanceLasers[controllerId].splice(i, 1);
                }
            }
        }
    }

    enableSinglePoint(value) {
        wv.effectController.SinglePointDistance = value;
        checkBoxSyncVR(singlePointDistanceFromControllerFeature.menuItemName, value);
        manageFeatureAndCallbackHandler(
            wv.mouseClickCallbackHandler,
            {callback: this.addSinglePointDistanceForMouse.bind(this)},
            value, singlePointDistanceFromControllerFeature.title,
            this.enableSinglePoint.bind(this), !value, false);
        manageFeatureAndCallbackHandler(
            wv.controllerTriggerDownCallbackhandler,
            {
                callback: this.addSinglePointDistanceForController.bind(this),
                animate: playPointStartAnimationForThisController,
                reverseCallbackHandler: wv.controllerTriggerReleasedCallbackhandler,
                reverseAnimate: playPointEndAnimationForThisController
            },
            value,
            singlePointDistanceFromControllerFeature.title, this.enableSinglePoint.bind(this), !value, false);
        if (value === true) {
            wv.effectController.MultiPointDistance = false;
            checkBoxSyncVR("MultiPointDistance", false);
        }
    }

    enableMultiPoint(value) {
        wv.effectController.MultiPointDistance = value;
        checkBoxSyncVR(multiPointDistanceFromControllerFeature.menuItemName, value);

        manageFeatureAndCallbackHandler(
            wv.mouseClickCallbackHandler,
            {callback: this.addSinglePointDistanceForMouse.bind(this)},
            value, multiPointDistanceFromControllerFeature.title,
            this.enableMultiPoint.bind(this), !value, false);
        manageFeatureAndCallbackHandler(
            wv.controllerTriggerDownCallbackhandler,
            {
                callback: this.addSinglePointDistanceForController.bind(this),
                animate: playPointEndAnimationForThisController,
                reverseCallbackHandler: wv.controllerTriggerReleasedCallbackhandler,
                reverseAnimate: playPointEndAnimationForThisController
            },
            value,
            multiPointDistanceFromControllerFeature.title, this.enableMultiPoint.bind(this), !value, false);
        if (value === true) {
            wv.effectController.SinglePointDistance = false;
            checkBoxSyncVR("SinglePointDistance", false);
        }
    }

    updatePointForExplosion(point, offset) {
        point.add(offset);
    }

    moveSelectedPoint(controllerId, entry, offset, bodyName) {
        let offsetVec, child_index;
        const volume = modelManager.getVolume(bodyName);
        if (volume !== undefined) {
            offsetVec = get_draw_offset_for_explosion(bodyName, CADModel.getById(volume.modelId));
        }

        if (offsetVec !== undefined) {
            offsetVec.negate();
            this.updatePointForExplosion(entry.selectedPoint, offsetVec);
        }

        entry.selectedPoint.applyMatrix4(offset);

        if (offsetVec !== undefined) {
            offsetVec.negate();
            this.updatePointForExplosion(entry.selectedPoint, offsetVec);
        }

        let firstVertex = entry.selectedPoint.clone();
        firstVertex = VR.scene.worldToLocal(firstVertex);
        entry.line.geometry.attributes.position.array[3] = firstVertex.x;
        entry.line.geometry.attributes.position.array[4] = firstVertex.y;
        entry.line.geometry.attributes.position.array[5] = firstVertex.z;
        entry.line.geometry.attributes.position.needsUpdate = true;
        entry.line.updateMatrixWorld(true);

        let controller = VR.scene.getObjectById(parseInt(controllerId));
        this.showDistanceFromController(controller, entry.selectedPoint, entry.tooltip, entry.selectionCircle);
    }

    movePointsOfThisBody(bodyName, offset, property) {
        for (let controllerId in this.distanceLasers) {
            for (let i in this.distanceLasers[controllerId]) {
                if (this.distanceLasers[controllerId][i][property] === bodyName) {
                    this.moveSelectedPoint(controllerId, this.distanceLasers[controllerId][i], offset, bodyName);

                    if (is_current_user_presenter()) {
                        const controller = VR.scene.getObjectById(parseInt(controllerId));
                        const controllerName = this.getControllerName(controller);
                        const payload = {
                            userId: this.distanceLasers[controllerId][i].userId,
                            controllerName: controllerName,
                            point: this.distanceLasers[controllerId][i].selectedPoint,
                            bodyName: bodyName,
                            property: property,
                            index: i
                        };

                        if (!controllerName) {
                            this.distanceControllerService.updateSelectedDistancePoint(payload);
                        }
                    }
                }
            }
        }
    }

    moveDistancePoint(parameters, offset) {
        const bodyName = parameters[0];

        if (modelManager.getVolume(bodyName) !== undefined) {
            this.movePointsOfThisBody(bodyName, offset, "branchId");
        }
        else if (getModelObject(bodyName) !== undefined) {
            this.movePointsOfThisBody(bodyName, offset, "modelId");
        }
    }

    getSelectionCircleScalingFactor() {
        return new THREE.Vector3(this.selectionCircleScaleX, this.selectionCircleScaleY, this.selectionCircleScaleZ);
    }

    scaleSelectionCircle(entry, dist) {
        if (entry === undefined) {
            return;
        }

        const scale_obj = this.getSelectionCircleScalingFactor();

        let scaleVec = new THREE.Vector3();
        scaleVec.set(scale_obj.x * dist, scale_obj.y * dist, scale_obj.z * dist);

        entry.scale.copy(scaleVec);
    }
}

export default DistanceController;
